import { IconClose } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DropZone } from "@/components/elements/DropZone/DropZone";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { FormField } from "@/components/elements/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import { EXTENSIONS, MIME_TYPES } from "@/constants/file";
import { TMatchForm } from "@/features/match/types/MatchForm";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { useDialog } from "@/hooks/useDialog";
import { black, blue, divider, text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TExtendFile } from "@/types/file";
import { makeUuid } from "@/utils/pieces";
import { Box, Collapse, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

type TMatchJobIntroductionManagerInfoFormProps = {
  defaultValues: Partial<TMatchForm>;
};

const MIN_OFFICE_COUNT = 1;

export const MatchJobIntroductionManagerInfoForm: FCC<TMatchJobIntroductionManagerInfoFormProps> = ({ defaultValues }) => {
  const methods = useFormContext<TMatchForm>();
  const { control, setValue, getValues } = methods;

  const initialCount = Math.max(defaultValues?.matchOffices?.length || MIN_OFFICE_COUNT, MIN_OFFICE_COUNT);
  const [officeCount, setOfficeCount] = useState(initialCount);
  const { expanded, toggle } = useCollapsible();

  const initialOfficeFiles = useMemo(() => {
    return (defaultValues?.matchOffices || []).map((office) => {
      const filePath = office?.appointmentNotificationFilePath;
      if (!filePath) return [];
      return [
        {
          id: makeUuid(),
          filePath,
          name: filePath.split("/").pop() || "",
          status: 1,
        } as unknown as TExtendFile,
      ];
    });
  }, [defaultValues?.matchOffices]);

  const [officeFiles, setOfficeFiles] = useState<TExtendFile[][]>(initialOfficeFiles);
  const confirmOverwriteDialog = useDialog();

  const handleOfficeFileChange = useCallback(
    (index: number) => async (value: TExtendFile | TExtendFile[] | null) => {
      const files = value ? (Array.isArray(value) ? value : [value]) : [];
      if (files.length === 0) {
        setOfficeFiles((prev) => {
          const updated = [...prev];
          updated[index] = [];
          return updated;
        });
        setValue(`matchOffices.${index}.appointmentNotificationFilePath`, null, { shouldDirty: true });
        // Clear file from form
        const currentFiles = getValues("officeAppointmentFiles") || [];
        const updatedFiles = [...currentFiles];
        updatedFiles[index] = null;
        setValue("officeAppointmentFiles", updatedFiles, { shouldDirty: true });
        return;
      }

      const file = files[0];
      if (file.filePath) {
        // Existing file — keep as-is
        setOfficeFiles((prev) => {
          const updated = [...prev];
          updated[index] = [file];
          return updated;
        });
        return;
      }

      // New raw file — confirm overwrite if a file already exists for this office
      if ((officeFiles[index] || []).length > 0) {
        const confirmed = await confirmOverwriteDialog.show();
        if (!confirmed) return;
      }

      // Proceed with overwrite — store File object for backend upload on save
      const displayFile = Object.assign(file, { id: makeUuid(), status: 1 }) as TExtendFile;
      setOfficeFiles((prev) => {
        const updated = [...prev];
        updated[index] = [displayFile];
        return updated;
      });
      setValue(`matchOffices.${index}.appointmentNotificationFilePath`, null, { shouldDirty: true });
      const currentFiles = getValues("officeAppointmentFiles") || [];
      const updatedFiles = [...currentFiles];
      updatedFiles[index] = file as unknown as File;
      setValue("officeAppointmentFiles", updatedFiles, { shouldDirty: true });
    },
    [setValue, getValues, officeFiles, confirmOverwriteDialog],
  );

  const handleRemoveOfficeFile = useCallback(
    (index: number) => () => {
      setOfficeFiles((prev) => {
        const updated = [...prev];
        updated[index] = [];
        return updated;
      });
      setValue(`matchOffices.${index}.appointmentNotificationFilePath`, null, { shouldDirty: true });
      const currentFiles = getValues("officeAppointmentFiles") || [];
      const updatedFiles = [...currentFiles];
      updatedFiles[index] = null;
      setValue("officeAppointmentFiles", updatedFiles, { shouldDirty: true });
    },
    [setValue, getValues],
  );

  const handleAddOffice = () => {
    setOfficeCount((prev) => prev + 1);
  };

  const handleRemoveOffice = () => {
    if (officeCount > MIN_OFFICE_COUNT) {
      const lastIndex = officeCount - 1;
      setValue(`matchOffices.${lastIndex}.officeName`, null, { shouldDirty: true });
      setValue(`matchOffices.${lastIndex}.fullName`, null, { shouldDirty: true });
      setValue(`matchOffices.${lastIndex}.trainingDate`, null, { shouldDirty: true });
      setValue(`matchOffices.${lastIndex}.appointmentNotificationFilePath`, null, { shouldDirty: true });
      setOfficeFiles((prev) => {
        const updated = [...prev];
        updated[lastIndex] = [];
        return updated;
      });
      setOfficeCount((prev) => prev - 1);
    }
  };

  return (
    <InnerBox showDivider={false} padding={3} borderColor={divider.high}>
      <CollapsibleSectionHeader title="職業紹介責任者情報" expanded={expanded} onToggle={toggle} />

      <Collapse in={expanded}>
        <Stack mt={2} alignItems="flex-start" spacing={2}>
          {Array.from({ length: officeCount }, (_, i) => (
            <Stack key={`match-office-${i}`} width="100%" spacing={2}>
              <Grid container columnGap={3}>
                <Grid item xs>
                  <FormField control={control} name={`matchOffices.${i}.officeName`} label={`${i + 1}. 事業所名`} required={i === 0}>
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>
                <Grid item xs>
                  <FormField control={control} name={`matchOffices.${i}.fullName`} label="氏名">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>
                <Grid item xs>
                  <FormField control={control} name={`matchOffices.${i}.trainingDate`} label="講習受講日">
                    <InputDatePicker placeholder="2020/03/15" />
                  </FormField>
                </Grid>
              </Grid>

              <Stack>
                <Typography variant="cap12" color={black[70]} mb={1}>
                  紹介責任者講習受講証明書
                </Typography>
                <DropZone
                  nonControl
                  value={officeFiles[i] || []}
                  onChange={handleOfficeFileChange(i)}
                  options={{ multiple: false }}
                  noClick
                  enableFileList={false}
                  rules={{
                    acceptMimeTypes: [...MIME_TYPES.PDF],
                    acceptFileTypesExtension: [...EXTENSIONS.PDF],
                    maxSize: 10 * 1000 * 1000,
                  }}
                  childrenRenderHtml={({ open }) => (
                    <>
                      <Typography variant="body14" color={text.secondary}>
                        アップロードするファイルをドラッグ&ドロップ
                      </Typography>
                      <Typography variant="body14" color={text.tertiary}>
                        （ファイル形式：PDFに対応）
                      </Typography>
                      <Typography variant="body14" color={text.secondary}>
                        または
                      </Typography>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          open();
                        }}
                      >
                        ファイルを選択する
                      </Button>
                    </>
                  )}
                />
                {(officeFiles[i] || []).length > 0 && (
                  <Stack mt={1}>
                    {(officeFiles[i] || []).map((file) => {
                      const fileProps = file.filePath ? { filePath: file.filePath } : { file: file as unknown as File };
                      return (
                        <Stack key={file.id} direction="row" alignItems="center" width="100%">
                          <FileItem
                            {...fileProps}
                            downloadable={!!file.filePath}
                            previewEnabled={!!file.filePath}
                            iconDownloadable={!!file.filePath}
                            hideIcon
                            files={file.filePath ? [{ filePath: file.filePath }] : undefined}
                          />
                          <Box flex={1} />
                          <IconBox onClick={handleRemoveOfficeFile(i)}>
                            <IconClose />
                          </IconBox>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Stack>
          ))}

          <Stack direction="row" spacing={2} alignItems="center">
            <Stack alignItems="center" direction="row" sx={{ cursor: "pointer" }} spacing={0.5} onClick={handleAddOffice}>
              <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
              <Typography variant="cap12Light">事業所を追加</Typography>
            </Stack>
            {officeCount > MIN_OFFICE_COUNT && (
              <Stack alignItems="center" direction="row" sx={{ cursor: "pointer" }} spacing={0.5} onClick={handleRemoveOffice}>
                <ClearIcon sx={{ color: black[80], fontSize: 18, display: "flex" }} />
                <Typography variant="cap12Light">削除</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Collapse>

      <ConfirmSaveModal
        title="上書きの確認"
        open={confirmOverwriteDialog.open}
        onOk={confirmOverwriteDialog.confirm}
        onClose={confirmOverwriteDialog.cancel}
      >
        <Typography component="p" variant="subtitle1">
          すでにアップロード済みですが上書きしてもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </InnerBox>
  );
};
